import React, { useState, useEffect, useContext, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faShoppingCart,
	faLock,
	faTag,
	faExclamationCircle,
	faCalendarAlt,
	faVideo
} from "@fortawesome/free-solid-svg-icons";
import SectionWrapper from "components/elements/SectionWrapper";
import styles from "./CartSection.module.scss";
import SmartLink from "components/SmartLink";
import { useCart } from "context/CartContext";
import Button from "components/elements/Button/Button";
import { sampleCartItems } from "data/sampleCartItems";
import { LoggerContext } from "context/LoggerContext";
import LazyImage from "components/elements/LazyImage/LazyImage";

/**
 * CartSection component - Displays shopping cart with items and summary
 * German standard checkout with VAT included in prices
 * Supports loading cart data from API using cartToken
 * 
 * @param {Object} props - Component props
 * @param {string} props.alignment - Text alignment (start, center, end)
 * @param {boolean} props.showCheckoutButton - Whether to show the checkout button
 * @param {string} props.checkoutUrl - URL for the checkout button
 * @param {boolean} props.loadSampleData - Whether to load sample data if cart is empty
 * @param {boolean} props.isEmbedded - Whether this cart is embedded in a checkout page
 * @param {boolean} props.hideChildrenWhenEmpty - Whether to hide children when cart is empty
 * @param {string} props.cartToken - Optional cart token for loading order data from API
 * @param {Object} props.data - Order data passed directly to component
 */
const CartSection = (props) => {
	const {
		alignment,
		showCheckoutButton = true,
		checkoutUrl = "/checkout",
		loadSampleData = false,
		isEmbedded = false,
		hideChildrenWhenEmpty = false,
		cartToken = null,
		children,
		data,
	} = props;

	const { cart, addItem, clearCart } = useCart();
	const { logDebug, logError } = useContext(LoggerContext);
	const [sampleDataLoaded, setSampleDataLoaded] = useState(false);
	const [discountCode, setDiscountCode] = useState("");
	const [orderData, setOrderData] = useState(data);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	
	// Use a ref to keep track of the current data ID to avoid infinite loops
	const processedDataIdRef = useRef(null);
	
	// Process order data from props only when data changes
	useEffect(() => {
		// Skip if data is not provided or we've already processed this exact data object
		if (!data || (processedDataIdRef.current === data.id)) {
			return;
		}
		
		// Update the ref with current data ID to prevent reprocessing
		processedDataIdRef.current = data.id;
		
		setIsLoading(true);
		setError(null);
		
		try {
			logDebug("Processing provided order data:", {
				hasData: !!data,
				orderNumber: data.order_number,
				items: data.items?.length || data.item?.article?.length,
				dataKeys: Object.keys(data)
			});
			
			// Store the order data
			setOrderData(data);
			
			// Clear existing cart and populate with order items
			clearCart();
			
			// Check if we have standard format or API format
			if (data.items && Array.isArray(data.items)) {
				// Standard format - add items directly
				data.items.forEach(item => {
					addItem({
						id: item.id || item.product_id,
						name: item.product_name || item.name,
						price: item.price || 0,
						quantity: item.quantity || 1,
						variant: item.variant_name || "",
						image: item.image || "",
						options: item.options || []
					});
				});
			} else if (data.item?.article && Array.isArray(data.item.article)) {
				// API format - transform to cart items
				data.item.article.forEach(article => {
					const instance = article.instance;
					const eventData = instance?.event;
					
					// Format price from API data
					const price = parseFloat(article.article_price?.total_gross_amount || 0);
					const quantity = parseInt(article.count || 1);
					
					// Create cart item
					const cartItem = {
						id: article.id,
						name: instance?.title || "Produkt",
						price: price,
						quantity: quantity,
						variant: eventData?.title || "",
						image: eventData?.feature_id ? `https://picsum.photos/id/${eventData.feature_id}/200` : null,
						options: [
							...(eventData?.date ? [{
								name: "Datum",
								value: formatDate(eventData.date)
							}] : []),
							eventData?.is_online ? {
								name: "Event-Typ",
								value: "Online"
							} : null
						].filter(Boolean)
					};
					
					addItem(cartItem);
				});
			}
		} catch (err) {
			logError(`Error processing order data: ${err.message}`);
			setError(`Fehler beim Verarbeiten der Bestelldaten: ${err.message}`);
		} finally {
			setIsLoading(false);
		}
	}, [data, logDebug, logError, addItem, clearCart]);

	// Handle sample data loading separately to avoid dependency conflicts
	useEffect(() => {
		if (loadSampleData && cart.items.length === 0 && !sampleDataLoaded && !cartToken && !data) {
			// Only load sample data if no cartToken and cart is empty
			sampleCartItems.forEach(item => {
				addItem(item);
			});
			setSampleDataLoaded(true);
		}
	}, [loadSampleData, cart.items.length, sampleDataLoaded, cartToken, data, addItem]);

	// Format price to include currency according to German standards (1.234,56 €)
	const formatPrice = (price) => {
		return `${price.toFixed(2).replace(".", ",")} €`;
	};

	// Handle discount code application
	const handleApplyDiscount = () => {
		// Placeholder for actual discount code logic
		alert(`Gutscheincode "${discountCode}" wurde angewendet (Beispiel)`);
	};

	// Empty cart component
	const renderEmptyCart = () => (
		<div className="bg-light rounded p-4 text-center">
			<FontAwesomeIcon
				icon={faShoppingCart}
				className="fs-1 text-secondary mb-3"
			/>
			<h3 className="mb-3">Ihr Warenkorb ist leer</h3>
			<p className="mb-3">
				Fügen Sie Produkte zum Warenkorb hinzu, um Ihre Bestellung fortzusetzen.
			</p>
		</div>
	);

	// Error message component
	const renderError = () => (
		<div className="bg-danger-subtle text-danger rounded p-4 text-center">
			<FontAwesomeIcon
				icon={faExclamationCircle}
				className="fs-1 mb-3"
			/>
			<h3 className="mb-3">Fehler beim Laden der Bestelldaten</h3>
			<p className="mb-3">{error}</p>
		</div>
	);

	// Calculate totals from API data if available
	const getTotals = () => {
		// Return cart totals by default
		let totals = {
			subtotal: cart.subtotal,
			tax: cart.tax,
			shipping: cart.shipping,
			total: cart.total,
			taxRate: cart.taxRate
		};
		
		// If we have order data with total price, use that
		if (orderData && orderData.total_price) {
			const totalPrice = orderData.total_price;
			
			totals = {
				subtotal: parseFloat(totalPrice.total_net_amount || 0),
				tax: parseFloat(totalPrice.total_tax_amount || 0),
				shipping: 0, // Default to 0 if not specified
				total: parseFloat(totalPrice.total_gross_amount || 0),
				taxRate: totals.taxRate // Keep the default tax rate
			};
		}
		
		return totals;
	};

	// Get computed totals
	const totals = getTotals();

	// Loading state
	if (isLoading) {
		return (
			<div className="text-center p-5">
				<div className="spinner-border" role="status">
					<span className="visually-hidden">Lädt...</span>
				</div>
				<p className="mt-3">Bestelldaten werden geladen...</p>
			</div>
		);
	}

	return (
		<div className={`text-${alignment || "start"}`}>
			<SectionWrapper
				className={`${styles.CartSection} ${isEmbedded ? "shadow-sm" : ""}`}
				fluid={false}
				{...props}
			>
				{/* Cart header */}
				{isEmbedded && (
					<div className="mb-4">
						<div className="text-success small fw-medium d-flex align-items-center gap-2 mb-1">
							<FontAwesomeIcon icon={faLock} /> Sichere Bezahlung
						</div>
						<h2 className="fs-4 fw-semibold mb-0">
							{cartToken ? "Bestellübersicht" : "Warenkorb"}
						</h2>
						{orderData && orderData.order_number && (
							<p className="text-secondary mt-2">
								Bestellnummer: <strong>{orderData.order_acronym || ""}-{orderData.order_number}</strong>
							</p>
						)}
					</div>
				)}

				{/* Error display */}
				{error && renderError()}

				{/* Cart content */}
				{!error && cart.items.length === 0 ? (
					renderEmptyCart()
				) : (
					<>
						{/* Product list */}
						<div className="mb-4">
							{cart.items.map((item) => (
								<div key={item.id} className="d-flex justify-content-between py-3 border-bottom">
									{/* Left side: Product image and details */}
									<div className="d-flex">
										{/* Product image with quantity badge */}
										<div className={`position-relative me-3 ${styles.itemImageWrapper}`}>
											{item.image ? (
												// <img
												// 	src={item.image}
												// 	alt={`${item.name}`}
												// 	className="rounded-circle"
												// 	width="64"
												// 	height="64"
												// />
                                                <LazyImage
                                                    src={item.image}
                                                    ratio="1x1"
                                                    fill_mode="cover"
                                                    round
                                                    alt={item.name}
                                                    className={`img-fluid object-fit-cover ${styles.gridImage}`}
                                                />
											) : (
												<div className="bg-secondary rounded-circle" style={{ width: 64, height: 64 }} />
											)}
											<span className={`position-absolute ${styles.itemQuantityBadge} badge rounded-circle bg-primary`}>
												{item.quantity}
											</span>
										</div>

										{/* Product details */}
										<div>
											<h3 className="fs-6 fw-medium mb-1">{item.name}</h3>
											{item.variant && (
												<p className="small text-secondary mb-1">
													<FontAwesomeIcon icon={item.variant.includes("Webinar") || item.variant.includes("Online") ? faVideo : faCalendarAlt} className="me-1" />
													{item.variant}
												</p>
											)}
											{item.options &&
												item.options.map((option, idx) => (
													<p key={idx} className="small text-secondary mb-1">
														<span className="fw-medium">{option.name}:</span>{" "}
														{option.value}
													</p>
												))}
										</div>
									</div>

									{/* Right side: Price, quantity and remove */}
									<div className="d-flex align-items-center gap-3">
										{/* Item price */}
										<div className="fw-semibold text-end" style={{ minWidth: '80px' }}>
											{formatPrice(item.price * item.quantity)}
										</div>

										{/* Quantity controls - disabled for order preview */}
										{/* {!cartToken && (
											<div className="d-flex border rounded overflow-hidden">
												<button
													type="button"
													className="btn btn-sm btn-light border-0 px-2"
													onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
													disabled={item.quantity <= 1 || isUpdating}
													aria-label="Menge verringern"
												>
													<FontAwesomeIcon icon={faMinus} />
												</button>
												<input
													type="text"
													value={item.quantity}
													className="border-0 text-center"
													style={{ width: '40px' }}
													onChange={(e) => {
														const value = parseInt(e.target.value);
														if (!isNaN(value)) {
															handleQuantityChange(item.id, value);
														}
													}}
													aria-label={`Menge von ${item.name}`}
												/>
												<button
													type="button"
													className="btn btn-sm btn-light border-0 px-2"
													onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
													disabled={isUpdating}
													aria-label="Menge erhöhen"
												>
													<FontAwesomeIcon icon={faPlus} />
												</button>
											</div>
										)} */}

										{/* Remove button - not shown in order preview */}
										{/* {!cartToken && (
											<button
												type="button"
												className="btn btn-sm btn-link text-danger"
												onClick={() => removeItem(item.id)}
												aria-label={`${item.name} aus dem Warenkorb entfernen`}
											>
												<FontAwesomeIcon icon={faTrash} />
											</button>
										)} */}
									</div>
								</div>
							))}
						</div>

						{/* Discount code input - not shown in order preview */}
						{!cartToken && (
							<div className="input-group mb-4">
								<input
									type="text"
									className="form-control"
									placeholder="Gutscheincode eingeben"
									value={discountCode}
									onChange={(e) => setDiscountCode(e.target.value)}
									aria-label="Gutscheincode"
								/>
								<button
									type="button"
									className="btn btn-dark"
									onClick={handleApplyDiscount}
								>
									<FontAwesomeIcon icon={faTag} className="me-2" /> Einlösen
								</button>
							</div>
						)}

						{/* Order summary */}
						<div className="bg-light p-3 rounded mb-4">
							<div className="d-flex justify-content-between small mb-2">
								<span>Zwischensumme</span>
								<span>{formatPrice(totals.subtotal)}</span>
							</div>

							{/* Show shipping info from order data or cart */}
							{totals.shipping > 0 && (
								<div className="d-flex justify-content-between small mb-2">
									<span>Versandkosten</span>
									<span>{formatPrice(totals.shipping)}</span>
								</div>
							)}

							{/* Order total */}
							<div className="d-flex justify-content-between fw-semibold pt-2 border-top mt-2">
								<span>Gesamtsumme</span>
								<span>{formatPrice(totals.total)}</span>
							</div>

							{/* Additional info from order data */}
							{orderData && orderData.payment && (
								<div className="pt-2 mt-2">
									<p className="mb-1"><strong>Zahlungsmethode:</strong> {orderData.payment}</p>
									{orderData.delivery_address && (
										<div className="mt-2">
											<p className="mb-1"><strong>Lieferadresse:</strong></p>
											<p className="mb-0 small">
												{orderData.delivery_address.first_name} {orderData.delivery_address.last_name}<br />
												{orderData.delivery_address.street}<br />
												{orderData.delivery_address.zip_code} {orderData.delivery_address.city}<br />
												{orderData.delivery_address.country}
											</p>
										</div>
									)}
								</div>
							)}

							<div className="text-secondary small mt-2">
								{totals.tax > 0 ? (
									`Inkl. ${formatPrice(totals.tax)} MwSt.`
								) : (
									`Alle Preise inkl. ${Math.round((orderData?.tax_amount || cart.taxRate) * 100) || 19}% MwSt.`
								)}
							</div>
						</div>

						{/* Order status info */}
						{orderData && orderData.status && (
							<div className={`alert ${getStatusAlertClass(orderData.status_id || orderData.status)}`} role="alert">
								<h4 className="alert-heading">Bestellstatus: {getStatusLabel(orderData.status_id || orderData.status)}</h4>
								<p>{getStatusDescription(orderData.status_id || orderData.status)}</p>
								{orderData.created_at && (
									<div className="mt-2">
										<p className="mb-1"><strong>Bestellt am:</strong> {formatDate(orderData.created_at)}</p>
									</div>
								)}
							</div>
						)}

						{/* Additional information */}
						<div className="bg-light-subtle p-3 rounded mb-4 border border-light">
							<strong className="d-block mb-1">Sicheres Einkaufen</strong>
							<p className="mb-0 small">
								Alle Transaktionen sind verschlüsselt und sicher. Wir schätzen Ihre Privatsphäre und halten die höchsten Sicherheitsstandards ein.
							</p>
						</div>

						{/* Checkout button - only shown in cart mode, not order preview */}
						{showCheckoutButton && !cartToken && (
							<div className="mt-4">
								<SmartLink to={checkoutUrl} className="text-decoration-none">
									<Button
										variant="primary"
										size="lg"
										fullWidth={true}
										aria-label="Zur Kasse gehen und Bestellung abschließen"
									>
										Zur Kasse
									</Button>
								</SmartLink>
							</div>
						)}
					</>
				)}

				{/* Additional components (e.g., checkout form) */}
				{(!hideChildrenWhenEmpty || cart.items.length > 0) && children}
			</SectionWrapper>
		</div>
	);
};

/**
 * Helper function to get an appropriate alert class for order status
 */
function getStatusAlertClass(status) {
	// If status is a number (status_id)
	if (typeof status === 'number') {
		switch (status) {
			case 1: return "alert-warning"; // Open/Pending
			case 2: return "alert-success"; // Confirmed/Completed
			case 3: return "alert-danger";  // Cancelled
			case 4: return "alert-info";    // Shipped
			default: return "alert-secondary";
		}
	}
	
	// If status is a string (status name)
	switch (status) {
		case "pending":
		case "Offen": return "alert-warning";
		case "confirmed": 
		case "Bestätigt": return "alert-success";
		case "cancelled":
		case "Storniert": return "alert-danger";
		case "shipped":
		case "Versendet": return "alert-info";
		default: return "alert-secondary";
	}
}

/**
 * Helper function to get human-readable status label
 */
function getStatusLabel(status) {
	// If status is a number (status_id)
	if (typeof status === 'number') {
		switch (status) {
			case 1: return "Offen";
			case 2: return "Bestätigt";
			case 3: return "Storniert";
			case 4: return "Versendet";
			default: return status.toString();
		}
	}
	
	// If status is a string
	if (typeof status === 'string') {
		return status; // Return the status label directly
	}
	
	// If status is a complex object, try to get its title
	if (status && typeof status === 'object' && status.title) {
		return status.title;
	}
	
	return "Unbekannt";
}

/**
 * Helper function to get status description
 */
function getStatusDescription(status) {
	// If status is a number (status_id)
	if (typeof status === 'number') {
		switch (status) {
			case 1: return "Ihre Bestellung wurde empfangen und wird derzeit bearbeitet.";
			case 2: return "Ihre Bestellung wurde bestätigt.";
			case 3: return "Ihre Bestellung wurde storniert.";
			case 4: return "Ihre Bestellung wurde versendet und ist auf dem Weg zu Ihnen.";
			default: return "Kein Statusdetail verfügbar.";
		}
	}
	
	// If status is a string
	switch (status) {
		case "pending":
		case "Offen": return "Ihre Bestellung wurde empfangen und wird derzeit bearbeitet.";
		case "confirmed": 
		case "Bestätigt": return "Ihre Bestellung wurde bestätigt.";
		case "cancelled":
		case "Storniert": return "Ihre Bestellung wurde storniert.";
		case "shipped":
		case "Versendet": return "Ihre Bestellung wurde versendet und ist auf dem Weg zu Ihnen.";
		default: return "Kein Statusdetail verfügbar.";
	}
}

/**
 * Helper function to format dates
 */
function formatDate(dateString) {
	// Check if dateString is valid
	if (!dateString) {
		return "N/A";
	}
	
	try {
		// Create date object
		const date = new Date(dateString);
		
		// Check if date is valid
		if (isNaN(date.getTime())) {
			return "N/A";
		}
		
		// Format the date and store in variable
		const formattedDate = date.toLocaleDateString('de-DE', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric'
		});
		
		// Return the formatted date
		return formattedDate;
	} catch (error) {
		// Handle any errors
		console.error('Error formatting date:', error);
		return "N/A";
	}
}

export default CartSection;
